/**
 * @voltxampere/web — fx state derivation (Sprint 29-5)
 *
 * Bridges the engine's transient output to the @voltxampere/fx package
 * so the canvas overlay can paint wire glow + meteor particles. Lives
 * in apps/web (not in @voltxampere/fx itself) so the fx package stays
 * engine-independent — it only consumes a small `BranchMeasurement[]`
 * shape that this module derives from the engine's output.
 *
 * Three-layer reduced-motion guard (S29-5 closure):
 *   1. User AYARLAR > Görünüm > "Akım akışı animasyonu" toggle off
 *   2. `prefers-reduced-motion: reduce` matchMedia
 *   3. DC mode (no transient result) — fxFrame is empty wires
 *
 * Any of the three forces `currentFxFrame.value.wires.size === 0`,
 * which the renderer interprets as "draw nothing extra".
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { computed, signal, type ReadonlySignal } from "@preact/signals-core";
import {
  computeFxFrame,
  type BranchMeasurement,
  type FxFrame,
  type NodeVoltageSample,
} from "@voltxampere/fx";
import type { Circuit, ComponentInstance } from "@voltxampere/schema";

import { currentCircuit } from "./circuit.js";
import { currentTransientResult, type TransientWireSample } from "./transient-runner.js";

/** User AYARLAR toggle — default ON, persisted to settings DB by canvas-prefs. */
export const fxAnimationEnabled = signal<boolean>(true);

/**
 * Sprint 30 — Voltage heatmap toggle. STATIC overlay (no animation),
 * so reduced-motion does NOT auto-disable it. Users with motion
 * sensitivity may still want the heatmap visible to read potentials.
 */
export const voltageHeatmapEnabled = signal<boolean>(true);

/**
 * Sprint 30 — Wire color gradient (start V → end V) toggle. Like the
 * heatmap, this is a static visual hint independent of animation, so
 * reduced-motion does not gate it.
 */
export const voltageGradientEnabled = signal<boolean>(true);

/** matchMedia('(prefers-reduced-motion: reduce)').matches mirror. Test seam exposes it. */
export const prefersReducedMotion = signal<boolean>(false);

/**
 * Hydrate the matchMedia signal + subscribe to OS preference changes.
 * Called from main.ts boot. Idempotent.
 */
let mqlBound = false;
export function bindReducedMotionMedia(): void {
  if (mqlBound) return;
  mqlBound = true;
  if (typeof window === "undefined") return;
  const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
  prefersReducedMotion.value = mql.matches;
  mql.addEventListener("change", (e) => {
    prefersReducedMotion.value = e.matches;
  });
}

/**
 * Setter — flips the AYARLAR toggle. Persistence (write-through) lives
 * in canvas-prefs.ts so the same code path that handles theme + wire
 * curve also handles fxAnimation. Sprint 30 v2 — persist writes the
 * full theme JSON (v1 fields + new v2 fields) atomically.
 */
export function setFxAnimationEnabled(enabled: boolean): void {
  fxAnimationEnabled.value = enabled;
  void persistThemePrefsAsync();
}

/** Sprint 30 — voltage heatmap on/off mutator + persist. */
export function setVoltageHeatmapEnabled(enabled: boolean): void {
  voltageHeatmapEnabled.value = enabled;
  void persistThemePrefsAsync();
}

/** Sprint 30 — wire color gradient on/off mutator + persist. */
export function setVoltageGradientEnabled(enabled: boolean): void {
  voltageGradientEnabled.value = enabled;
  void persistThemePrefsAsync();
}

/**
 * Lazy import + invoke `persistThemePrefs()` from canvas-prefs.ts.
 * Dynamic so we avoid the static circular dependency
 * (canvas-prefs.ts already imports from fx-state.ts for hydrate).
 */
async function persistThemePrefsAsync(): Promise<void> {
  try {
    const mod = await import("./canvas-prefs.js");
    mod.persistThemePrefs();
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[VXA fx-state] persist trigger failed:", err);
    }
  }
}

const EMPTY_FX_FRAME: FxFrame = { t: 0, wires: new Map(), components: new Map() };

/**
 * Derived fx state. Recomputes whenever the user's last transient
 * sample, the active circuit, or the reduced-motion guards flip.
 *
 * The frame uses the FINAL transient sample (last time step) so the
 * overlay reflects the steady-state glow + particle density. Sprint 32
 * time scrubber will replace `lastSample` with a scrubber-bound index.
 */
export const currentFxFrame: ReadonlySignal<FxFrame> = computed(() => {
  if (!fxAnimationEnabled.value || prefersReducedMotion.value) {
    return EMPTY_FX_FRAME;
  }
  const circuit = currentCircuit.value;
  const result = currentTransientResult.value;
  if (!circuit || !result || result.timeSeries.length === 0) {
    return EMPTY_FX_FRAME;
  }
  const lastSample = result.timeSeries[result.timeSeries.length - 1];
  if (!lastSample) return EMPTY_FX_FRAME;
  const measurements = extractBranchMeasurements(circuit, lastSample);
  return computeFxFrame({ t: lastSample.t, circuit, measurements });
});

/**
 * Sprint 30 — derived voltage samples for the heatmap overlay.
 *
 * Recomputes whenever the user's last transient sample, the active
 * circuit, or the heatmap toggle flips. Returns an empty array when
 * the toggle is off OR no transient result is available (DC mode).
 *
 * Reduced-motion does NOT gate this signal — heatmap is a static visual
 * cue (no animation), so users with motion sensitivity still benefit
 * from seeing potential bands.
 */
export const currentVoltageSamples: ReadonlySignal<readonly NodeVoltageSample[]> = computed(() => {
  if (!voltageHeatmapEnabled.value && !voltageGradientEnabled.value) return [];
  const circuit = currentCircuit.value;
  const result = currentTransientResult.value;
  if (!circuit || !result || result.timeSeries.length === 0) return [];
  const lastSample = result.timeSeries[result.timeSeries.length - 1];
  if (!lastSample) return [];
  return nodeSamplesFromCircuit(circuit, lastSample.nodeVoltages);
});

/**
 * Build a `NodeVoltageSample[]` from a transient sample + circuit
 * topology. Each net's representative position = the first connected
 * component terminal's world coordinates. Ground nets are filtered
 * out — they're the 0V reference and would dominate the palette
 * normalisation if included.
 *
 * The renderer uses these samples for two things:
 *   - voltage heatmap (per-node radial halo)
 *   - wire color gradient (start/end net voltages on each wire)
 */
export function nodeSamplesFromCircuit(
  circuit: Circuit,
  nodeVoltages: Record<string, number>,
): NodeVoltageSample[] {
  const groundNetIds = new Set(circuit.nets.filter((n) => n.isGround).map((n) => n.id));
  // Build netId → first-seen terminal world position
  const netPositions = new Map<string, { x: number; y: number }>();
  for (const component of circuit.components) {
    for (const terminal of component.terminals) {
      if (netPositions.has(terminal.netId)) continue;
      netPositions.set(terminal.netId, terminal.position);
    }
  }
  const samples: NodeVoltageSample[] = [];
  for (const net of circuit.nets) {
    if (groundNetIds.has(net.id)) continue;
    const position = netPositions.get(net.id);
    if (!position) continue;
    const voltage = nodeVoltages[net.id];
    if (typeof voltage !== "number" || !Number.isFinite(voltage)) continue;
    samples.push({ netId: net.id, position, voltage });
  }
  return samples;
}

/**
 * Build a `BranchMeasurement[]` from the engine's transient sample +
 * circuit topology.
 *
 * Strategy (Sprint 29 — 2-pin priority):
 *   - Voltage source / inductor: take from `sample.sourceCurrents[id]`
 *     (engine emits these directly via the augmented MNA row).
 *   - Resistor: Ohm's law on its two terminals' node voltages — engine
 *     does not currently emit branch current for resistors so we
 *     compute it locally from `(V_p1 - V_p2) / R`. Sign convention:
 *     positive when current flows INTO terminal 0 (matches engine's
 *     SPICE passive-sign convention).
 *   - Other components (diode, BJT, MOSFET, capacitor, etc.):
 *     Sprint 30+ — for now they have no measurement entry and the wire
 *     solver falls back to 0 current (renderer paints no glow / no
 *     particles). KCL on the connected nets propagates resistor /
 *     source current to wires sharing the same net.
 */
export function extractBranchMeasurements(
  circuit: Circuit,
  sample: TransientWireSample,
): BranchMeasurement[] {
  const out: BranchMeasurement[] = [];
  for (const component of circuit.components) {
    const I = computeComponentBranchCurrent(component, sample);
    if (I !== null) {
      out.push({ id: component.id, current: I });
    }
  }
  return out;
}

function computeComponentBranchCurrent(
  component: ComponentInstance,
  sample: TransientWireSample,
): number | null {
  // 1. Engine-emitted source current (V sources, inductors)
  const sourced = sample.sourceCurrents[component.id];
  if (typeof sourced === "number") return sourced;

  // 2. Resistor: Ohm's law on terminal node voltages
  if (component.type === "resistor") {
    const t0 = component.terminals[0];
    const t1 = component.terminals[1];
    const R = readNumericParam(component, "resistance");
    if (!t0 || !t1 || R === null || R <= 0) return null;
    const V0 = sample.nodeVoltages[t0.netId] ?? 0;
    const V1 = sample.nodeVoltages[t1.netId] ?? 0;
    return (V0 - V1) / R;
  }

  // 3. Other 2-pin / N-pin components — Sprint 30+ scope
  return null;
}

function readNumericParam(component: ComponentInstance, key: string): number | null {
  const v = component.params[key];
  if (typeof v === "number" && Number.isFinite(v)) return v;
  return null;
}

/** Test cleanup. */
export function __resetFxStateForTests(): void {
  fxAnimationEnabled.value = true;
  voltageHeatmapEnabled.value = true;
  voltageGradientEnabled.value = true;
  prefersReducedMotion.value = false;
  mqlBound = false;
}
